import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import NotificationService from "../../services/NotificationService";
import { formatLedgerAmount } from "./clientLedgerTypes";

export const newReminderRequestId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
export const reminderReason = (tr, code) => tr(`manage:paymentReminderV1.reasons.${code}`, { defaultValue: tr("manage:paymentReminderV1.unknownReason", { code }) });
const terminal = ["SENT", "FAILED", "SKIPPED", "UNKNOWN"];

export default function PaymentReminderPreviewModal({ row, onHide, service }) {
  const { t: tr } = useTranslation();
  const t = key => tr(`manage:paymentReminderV1.${key}`);
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [request, setRequest] = useState(null);
  const [pollRetry, setPollRetry] = useState(0);
  const alive = useRef(true);
  const submitting = useRef(false);
  const command = useRef(null);
  useEffect(() => {
    alive.current = true;
    service.getPaymentReminderMessage(row.clientId, row.obligationId).then(value => {
      if (alive.current) setPreview(value);
    }).catch(err => { if (alive.current) setError(err.message || t("error")); });
    return () => { alive.current = false; };
  }, [row.clientId, row.obligationId, service]);

  useEffect(() => {
    if (!request || terminal.includes(request.status)) return undefined;
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const result = await service.getPaymentReminderRequest(request.requestId);
        if (cancelled) return;
        setRequest(result);
        if (!terminal.includes(result.status)) timer = setTimeout(poll, 3000);
      } catch (err) {
        if (!cancelled) setError(err.message || t("error"));
      }
    };
    timer = setTimeout(poll, 1500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [request && request.requestId, pollRetry, service]);

  const submit = async () => {
    if (submitting.current || !preview || !confirmed || reason.trim().length < 3 || request) return;
    if (!command.current) {
      command.current = {
        requestId: newReminderRequestId(),
        sourceNotificationId: preview.sourceNotificationId,
        previewFingerprint: preview.previewFingerprint,
        reason: reason.trim(),
      };
    }
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await service.resendPaymentReminder(command.current);
      if (alive.current) setRequest(result);
    } catch (err) {
      if (alive.current) {
        if (err.code === 409) {
          setError(["preview_changed", "superseded_obligation"].includes(err.message) ? t("changed") : reminderReason(tr, err.message));
        } else {
          setError(err.message || t("error"));
        }
        if (err.code === 409) setPreview(null);
      }
    } finally {
      submitting.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const decision = preview && preview.decision;
  const canResend = decision && decision.status === "eligible" && preview.sourceNotificationId;
  return (
    <Modal show onHide={onHide} size="lg" aria-labelledby="reminder-preview-title">
      <Modal.Header><Modal.Title id="reminder-preview-title">{t("preview")}</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        {!preview && !error && <p>{t("loading")}</p>}
        {preview && (
        <>
          <p>{t("amount")}: {formatLedgerAmount(decision.amountDue)}</p>
          <p>{t("decision")}: {t(decision.status)}</p>
          {(decision.reasonCodes || []).map(code => <p key={code}>{reminderReason(tr, code)}</p>)}
          {preview.message && (
          <>
            <p>{t("to")}: {preview.message.to}</p><p>{t("subject")}: {preview.message.subject}</p>
            <pre style={{ whiteSpace: "pre-wrap" }}>{preview.message.text}</pre>
          </>
          )}
          {!preview.sourceNotificationId && <p>{t("noSource")}</p>}
          {canResend && !request && (
          <>
            <label htmlFor="reminder-reason">{t("reason")}</label>
            <textarea id="reminder-reason" className="form-control" maxLength={1000} value={reason} disabled={busy || !!command.current} onChange={e => setReason(e.target.value)} />
            <label className="mt-3" htmlFor="reminder-confirm">
              <input id="reminder-confirm" type="checkbox" checked={confirmed} disabled={busy || !!command.current} onChange={e => setConfirmed(e.target.checked)} /> {t("confirm")}
            </label>
          </>
          )}
        </>
        )}
        {request && (
        <div role="status">
          <p>{t(`states.${request.status}`)}</p>
          {(request.reasonCodes || []).map(code => <p key={code}>{reminderReason(tr, code)}</p>)}
          {request.error && <p>{request.error}</p>}
          {error && !terminal.includes(request.status) && <button type="button" className="btn manage-teal-button" onClick={() => { setError(""); setPollRetry(pollRetry + 1); }}>{t("retry")}</button>}
        </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn manage-button manage-teal-button" onClick={onHide}>{tr("common:back")}</button>
        {canResend && !request && (
        <button
          type="button"
          className="btn manage-button manage-red-button"
          disabled={busy || !confirmed || reason.trim().length < 3}
          onClick={submit}
        >{t("resend")}
        </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
PaymentReminderPreviewModal.propTypes = { row: PropTypes.object.isRequired, onHide: PropTypes.func.isRequired, service: PropTypes.object };
PaymentReminderPreviewModal.defaultProps = { service: NotificationService };
